/**
 * Shared utilities for form-associated custom elements.
 *
 * All six input controls (text-input, numeric-input, select, checkbox,
 * radio-group, date-picker) set `static formAssociated = true` and call
 * `attachInternals()` behind a guard — jsdom 26 does not implement
 * `ElementInternals` and the unit suite runs in jsdom. Every use of
 * `this._internals` is optional-chained, and the control stays fully
 * functional without it.
 */

import type { QdsValidity, QdsControlSize, QdsValidateOn } from '../../types/index';

let idCounter = 0;

/** Generates a unique id per instance so multiple controls never collide. */
export function generateControlId(prefix: string): string {
  return `qds-${prefix}-${++idCounter}`;
}

/**
 * Re-applies a property set on the element BEFORE it was upgraded to a
 * custom element (e.g. a consumer script runs before this component's
 * module has registered). Without this, the pre-upgrade assignment lands
 * as a plain own-property that shadows the class's getter/setter forever,
 * silently dropping the value — the classic "properties set before
 * upgrade" web-components pitfall. Call once per affected property in
 * `connectedCallback`, before reading that property.
 */
export function upgradeProperty(host: HTMLElement, prop: string): void {
  if (Object.prototype.hasOwnProperty.call(host, prop)) {
    const value = (host as unknown as Record<string, unknown>)[prop];
    delete (host as unknown as Record<string, unknown>)[prop];
    (host as unknown as Record<string, unknown>)[prop] = value;
  }
}

/** Calls `attachInternals()` behind a try/catch so jsdom doesn't throw. */
export function attachInternalsSafe(host: HTMLElement): ElementInternals | undefined {
  try {
    return host.attachInternals();
  } catch {
    return undefined;
  }
}

/** True when ElementInternals is available on the host (i.e. not jsdom). */
export function hasInternals(internals: ElementInternals | undefined): internals is ElementInternals {
  return !!internals;
}

/**
 * Tracks validity with a `QdsValidity` fallback when `ElementInternals` is
 * unavailable. When internals ARE available, delegates to them so the
 * browser's built-in form validation UI works.
 */
export class ValidityTracker {
  private _internals: ElementInternals | undefined;
  private _flags: QdsValidity = {
    valid: true,
    valueMissing: false,
    typeMismatch: false,
    patternMismatch: false,
    rangeUnderflow: false,
    rangeOverflow: false,
    stepMismatch: false,
    customError: false,
  };
  private _message = '';

  constructor(internals: ElementInternals | undefined) {
    this._internals = internals;
  }

  get validity(): QdsValidity {
    if (hasInternals(this._internals)) {
      const v = this._internals.validity;
      return {
        valid: v.valid,
        valueMissing: v.valueMissing,
        typeMismatch: v.typeMismatch,
        patternMismatch: v.patternMismatch,
        rangeUnderflow: v.rangeUnderflow,
        rangeOverflow: v.rangeOverflow,
        stepMismatch: v.stepMismatch,
        customError: v.customError,
      };
    }
    return { ...this._flags };
  }

  get valid(): boolean {
    return this.validity.valid;
  }

  get message(): string {
    return this._message;
  }

  /**
   * Sets validity flags. When internals are available, calls
   * `setValidity` so the browser participates. Otherwise updates the
   * local fallback.
   */
  setValidity(flags: Partial<QdsValidity>, message: string = ''): void {
    const merged = { ...this._flags, ...flags };
    this._flags = merged;
    this._message = message;

    if (hasInternals(this._internals)) {
      const valid = merged.valid;
      // Build a ValidityState-like for setValidity — pass null when valid
      if (valid && !message) {
        try {
          this._internals.setValidity({});
        } catch {
          /* noop */
        }
      } else {
        try {
          this._internals.setValidity(merged as unknown as ValidityState, message || undefined);
        } catch {
          /* noop */
        }
      }
    }
  }

  /** Sets a custom error message. Empty string clears it. */
  setCustomValidity(message: string): void {
    if (message) {
      this.setValidity({ customError: true, valid: false }, message);
    } else {
      const hasOtherErrors = this._flags.valueMissing
        || this._flags.typeMismatch
        || this._flags.patternMismatch
        || this._flags.rangeUnderflow
        || this._flags.rangeOverflow
        || this._flags.stepMismatch;
      this.setValidity(
        { customError: false, valid: !hasOtherErrors },
        '',
      );
    }
  }

  checkValidity(): boolean {
    if (hasInternals(this._internals)) {
      return this._internals.checkValidity();
    }
    return this._flags.valid;
  }

  reportValidity(): boolean {
    if (hasInternals(this._internals)) {
      return this._internals.reportValidity();
    }
    return this._flags.valid;
  }
}

/** Dispatches a `qds-validity-change` event (bubbles, composed). */
export function dispatchValidityChange(
  host: HTMLElement,
  tracker: ValidityTracker,
): void {
  const validity = tracker.validity;
  host.dispatchEvent(new CustomEvent('qds-validity-change', {
    bubbles: true,
    composed: true,
    detail: {
      component: host,
      valid: validity.valid,
      validity,
      message: tracker.message,
    },
  }));
}

/** Boolean attribute helper — returns true for "", "true", "" (presence). */
export function isAttrTruthy(value: string | null): boolean {
  return value !== null && value !== 'false';
}

/** Reads a boolean attribute and reflects it back. */
export function reflectBoolean(host: HTMLElement, name: string, value: boolean): void {
  if (value) {
    host.setAttribute(name, '');
  } else {
    host.removeAttribute(name);
  }
}

/** Parses the size attribute with a default. */
export function parseSize(value: string | null): QdsControlSize {
  if (value === 'sm' || value === 'lg') return value;
  return 'md';
}

/** Parses the validate-on attribute with a default. */
export function parseValidateOn(value: string | null): QdsValidateOn {
  if (value === 'input' || value === 'submit') return value;
  return 'blur';
}

/**
 * Shared CSS for form control surfaces (input, trigger, etc.).
 * Every form control draws from the same visual language: same height,
 * border, focus ring, states. This ensures a text-input and a select side
 * by side are indistinguishable in their resting frame.
 */
export function getFormControlStyles(): string {
  return `
    :host {
      display: inline-block;
      --qds-control-height: var(--qds-control-height-md, 40px);
    }
    :host([size="sm"]) {
      --qds-control-height: var(--qds-control-height-sm, 32px);
    }
    :host([size="lg"]) {
      --qds-control-height: var(--qds-control-height-lg, 48px);
    }
    @media (pointer: coarse) {
      :host {
        --qds-control-height: max(var(--qds-control-height-md, 40px), 44px);
      }
      :host([size="sm"]) {
        --qds-control-height: max(var(--qds-control-height-sm, 32px), 44px);
      }
    }

    .qds-control-surface {
      display: flex;
      align-items: center;
      height: var(--qds-control-height);
      border: 1px solid var(--theme-border-primary, #E4E9F2);
      border-radius: var(--theme-radius-sm, 4px);
      background: var(--theme-background-primary, #fff);
      color: var(--theme-text-primary, #333);
      padding: 0 var(--theme-space-3, 12px);
      box-sizing: border-box;
      transition: border-color var(--transition-speed-fast, 0.15s),
                  background-color var(--transition-speed-fast, 0.15s),
                  box-shadow var(--transition-speed-fast, 0.15s);
      font-size: 14px;
      font-family: inherit;
    }
    :host([size="lg"]) .qds-control-surface {
      padding: 0 var(--theme-space-4, 16px);
    }
    :host([size="sm"]) .qds-control-surface {
      font-size: 14px;
    }

    .qds-control-surface:hover {
      border-color: var(--theme-border-secondary, #AFBBD1);
    }

    .qds-control-surface:focus-within,
    .qds-control-surface:focus {
      border-color: var(--theme-accent-primary, #445E89);
      box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
      outline: none;
    }

    :host([readonly]) .qds-control-surface {
      background: var(--theme-background-secondary, #F8F9FA);
      cursor: default;
    }

    :host([disabled]), :host(:disabled) {
      opacity: 0.55;
      cursor: not-allowed;
      pointer-events: none;
    }
    :host([disabled]) .qds-control-surface, :host(:disabled) .qds-control-surface {
      color: var(--theme-text-tertiary, #6A6A6A);
    }

    :host([invalid]) .qds-control-surface,
    :host([data-invalid]) .qds-control-surface {
      border-color: var(--theme-error, #8B1F00);
      background: var(--theme-background-error-subtle, rgba(139, 31, 0, 0.10));
    }
    :host([invalid]) .qds-control-surface:focus-within,
    :host([data-invalid]) .qds-control-surface:focus-within {
      border-color: var(--theme-error, #8B1F00);
      box-shadow: 0 0 0 2px var(--theme-error, rgba(139, 31, 0, 0.25));
    }

    .qds-label {
      display: block;
      font-size: 14px;
      color: var(--theme-text-secondary, #555);
      margin-bottom: var(--theme-space-1, 4px);
      font-weight: 500;
    }
    :host([size="sm"]) .qds-label {
      font-size: 13px;
    }
    .qds-label .qds-required-marker {
      color: var(--theme-error, #8B1F00);
      margin-left: 2px;
      aria-hidden: true;
    }

    .qds-hint {
      font-size: 13px;
      color: var(--theme-text-tertiary, #6A6A6A);
      margin-top: var(--theme-space-1, 4px);
      line-height: 1.4;
    }

    .qds-error {
      font-size: 13px;
      color: var(--theme-error, #8B1F00);
      margin-top: var(--theme-space-1, 4px);
      line-height: 1.4;
      aria-live: polite;
    }
  `;
}
